import { Suspense, lazy, useState, useCallback, useEffect } from 'react';
import { SettingsDrawer } from 'calc-nav';
import { SidebarNav } from 'calc-sidebar';
import type { SidebarItemDef } from 'calc-sidebar';
import { AboutModal, buildAboutConfig } from '@ascend/about-modal';
import appIdentity from 'virtual:app-identity';
import { Header } from './Header';
import { SettingsBar } from './SettingsBar';
import { OnboardingBanner } from './OnboardingBanner';
import { CourseEntryForm } from './CourseEntryForm';
import { CourseTable } from './CourseTable';
import { GPAComparisonPanel } from './GPAComparisonPanel';
import { ValidationPanel } from './ValidationPanel';
import { GPAReportingGuide } from './GPAReportingGuide';
import { HonorsCapCounter } from './HonorsCapCounter';
import { ExportBar } from './ExportBar';
import { SubjectAreaBreakdown } from './SubjectAreaBreakdown';
import { ScenarioPanel } from './ScenarioPanel';
import { EdgeCasePanel } from './EdgeCasePanel';
import { CommonAppBenchmarks } from './CommonAppBenchmarks';
import { CollegeShortlist } from './CollegeShortlist';
import { SchoolContextPanel } from './SchoolContextPanel';
import { VersionHistory } from './VersionHistory';
import { BulkImport } from './BulkImport';
import { SessionManager } from './SessionManager';
import { ErrorBoundary } from './ErrorBoundary';
import { ToastBar } from './ToastBar';
import { FeedbackForm, FeedbackButton } from './FeedbackForm/FeedbackForm';
import type { UserFeedback } from '@/utils/userFeedback';
import { useAppStore } from '@/store/useAppStore';
import { useToast } from '@/hooks/useToast';

// R8 — Code-split heavy components (chart.js, jspdf)
const GPATrajectoryChart = lazy(() =>
  import('./GPATrajectoryChart').then((m) => ({ default: m.GPATrajectoryChart }))
);
const RigorDistributionChart = lazy(() =>
  import('./RigorDistributionChart').then((m) => ({ default: m.RigorDistributionChart }))
);
const UCCampusBenchmarks = lazy(() =>
  import('./UCCampusBenchmarks').then((m) => ({ default: m.UCCampusBenchmarks }))
);

function ChartLoader() {
  return (
    <div className="flex items-center justify-center rounded-lg border border-border bg-surface p-8">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="ml-2 text-sm text-[var(--color-text-muted)]">Loading chart…</span>
    </div>
  );
}

// ── Sidebar icons (minimal 16x16 SVG, inherit currentColor) ──

function OverviewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="5.5" height="5.5" rx="1" />
      <rect x="8.5" y="2" width="5.5" height="5.5" rx="1" />
      <rect x="2" y="8.5" width="5.5" height="5.5" rx="1" />
      <rect x="8.5" y="8.5" width="5.5" height="5.5" rx="1" />
    </svg>
  );
}

function AnalysisIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 13.5h12" />
      <rect x="3" y="8.5" width="2.2" height="5" rx="0.5" />
      <rect x="6.9" y="5" width="2.2" height="8.5" rx="0.5" />
      <rect x="10.8" y="2.5" width="2.2" height="11" rx="0.5" />
    </svg>
  );
}

function ScenariosIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 4.5h6M2 8h9M2 11.5h5" />
      <path d="M11.5 9.5l2.5 2-2.5 2" />
      <path d="M14 11.5H9" />
    </svg>
  );
}

function BenchmarksIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 1.5v1M8 13.5v1M3 8H2M14 8h-1M4.2 4.2l-.7-.7M12.5 12.5l-.7-.7M11.8 4.2l.7-.7M3.5 12.5l.7-.7" />
      <circle cx="8" cy="8" r="3.2" />
    </svg>
  );
}

function ToolsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.2 3.6a2.4 2.4 0 0 0-3.1 3.1l-4 4a1 1 0 0 0 1.4 1.4l4-4a2.4 2.4 0 0 0 3.1-3.1l-1.5 1.5-1.4-.2-.2-1.4 1.4-1.4z" />
      <path d="M9.5 9.5l3.5 3.5" />
    </svg>
  );
}

// ── Tab content components ──
// calc-nav keeps every panel mounted (inactive ones hidden via CSS), so each
// tab's content is extracted into a stable component.

function OverviewContent() {
  return (
    <ErrorBoundary inline>
      <div className="space-y-6">
        <OnboardingBanner />
        <section>
          <CourseEntryForm />
        </section>
        <section>
          <CourseTable />
        </section>
        <section>
          <h2 className="mb-4 font-serif text-2xl font-semibold tracking-tight text-[var(--color-text)] sm:text-3xl">
            GPA Results
          </h2>
          <GPAComparisonPanel />
        </section>
        <section>
          <GPAReportingGuide />
        </section>
        <div className="grid gap-6 lg:grid-cols-3">
          <HonorsCapCounter />
          <div className="lg:col-span-2">
            <ValidationPanel />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}

function AnalysisContent() {
  return (
    <div className="space-y-6">
      <section>
        <ErrorBoundary>
          <Suspense fallback={<ChartLoader />}>
            <GPATrajectoryChart />
          </Suspense>
        </ErrorBoundary>
      </section>
      <section>
        <ErrorBoundary>
          <Suspense fallback={<ChartLoader />}>
            <RigorDistributionChart />
          </Suspense>
        </ErrorBoundary>
      </section>
      <section>
        <SubjectAreaBreakdown />
      </section>
      <section>
        <EdgeCasePanel />
      </section>
    </div>
  );
}

function ScenariosContent() {
  return (
    <ErrorBoundary inline>
      <div className="space-y-6">
        <section>
          <ScenarioPanel />
        </section>
      </div>
    </ErrorBoundary>
  );
}

function BenchmarksContent() {
  return (
    <ErrorBoundary inline>
      <div className="space-y-6">
        <section>
          <ErrorBoundary>
            <Suspense fallback={<ChartLoader />}>
              <UCCampusBenchmarks />
            </Suspense>
          </ErrorBoundary>
        </section>
        <section>
          <CommonAppBenchmarks />
        </section>
        <section>
          <CollegeShortlist />
        </section>
        <section>
          <SchoolContextPanel />
        </section>
      </div>
    </ErrorBoundary>
  );
}

function ToolsContent() {
  return (
    <ErrorBoundary inline>
      <div className="space-y-6">
        <section>
          <VersionHistory />
        </section>
        <section>
          <BulkImport />
        </section>
        <section>
          <SessionManager />
        </section>
      </div>
    </ErrorBoundary>
  );
}

// App identity for the About modal, sourced from app.toml via the
// @ascend/about-modal-vite plugin (virtual:app-identity module).
const aboutConfig = buildAboutConfig(appIdentity, 'VITE_APP_');

export function Layout() {
  const settingsOpen = useAppStore((s) => s.settingsOpen);
  const toggleSettings = useAppStore((s) => s.toggleSettings);
  const { toast, handleAction, dismiss } = useToast();

  // About modal visibility.
  const [aboutOpen, setAboutOpen] = useState(false);

  // ── In-app feedback (feedback-widget mailto pattern) ──
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackConfig, setFeedbackConfig] = useState<{
    type?: UserFeedback['type'] | undefined;
    category?: string | undefined;
    error?: string | undefined;
  }>({});

  const handleOpenFeedback = useCallback((
    type?: UserFeedback['type'],
    category?: string,
    error?: string,
  ) => {
    setFeedbackConfig({ type, category, error });
    setFeedbackOpen(true);
  }, []);

  const handleCloseFeedback = useCallback(() => {
    setFeedbackOpen(false);
    setFeedbackConfig({});
  }, []);

  // Allow ErrorBoundary (a class component, no hooks) to open feedback via a
  // custom window event pre-filled with the caught error.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as {
        type?: UserFeedback['type'];
        category?: string;
        error?: string;
      };
      handleOpenFeedback(detail.type, detail.category, detail.error);
    };
    window.addEventListener('open-feedback', handler);
    return () => window.removeEventListener('open-feedback', handler);
  }, [handleOpenFeedback]);

  const navItems: SidebarItemDef[] = [
    { id: 'overview', label: 'Overview', icon: <OverviewIcon />, content: <OverviewContent /> },
    { id: 'analysis', label: 'Analysis', icon: <AnalysisIcon />, content: <AnalysisContent /> },
    { id: 'scenarios', label: 'Scenarios', icon: <ScenariosIcon />, content: <ScenariosContent /> },
    { id: 'benchmarks', label: 'Benchmarks', icon: <BenchmarksIcon />, content: <BenchmarksContent /> },
    { id: 'tools', label: 'Tools', icon: <ToolsIcon />, content: <ToolsContent /> },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-[var(--color-bg)]">
      {/* Skip navigation link (R15) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-0 focus:top-0 focus:z-50 focus:bg-surface focus:p-4 focus:shadow-lg focus:outline-none">
        Skip to main content
      </a>
      <Header />
      <main id="main-content" className="flex-1 px-4 py-6 sm:px-6 pb-20">
        <div className="mx-auto max-w-7xl">
          {/* Sidebar Navigation — managed by calc-sidebar (URL hash persisted as #nav/<item>).
              The section namespace "nav" is distinct from the ScenarioPanel's
              nested calc-nav TabGroup (section="scenarios"). SidebarNav renders
              both the sidebar and the content panels (a complete layout primitive). */}
          <SidebarNav
            section="nav"
            stickyTop="56px"
            items={navItems}
          />
        </div>
      </main>
      {/* Sticky export bar (R35) */}
      <div className="sticky bottom-0 border-t border-border bg-surface px-4 py-2 shadow-lg sm:px-6 export-bar">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <p className="text-sm text-[var(--color-text-muted)]">
            All calculations run locally. No data is sent to any server.
          </p>
          <ExportBar />
        </div>
      </div>
      {/* R27 — Undo toast */}
      {toast && <ToastBar toast={toast} onAction={handleAction} onDismiss={dismiss} />}
      {/* Settings drawer (calc-nav) — visibility driven by the store's settingsOpen */}
      <SettingsDrawer
        isOpen={settingsOpen}
        onClose={toggleSettings}
        title="Settings"
      >
        <SettingsBar onOpenAbout={() => setAboutOpen(true)} />
      </SettingsDrawer>
      {/* About modal (app identity from app.toml) */}
      <AboutModal
        isOpen={aboutOpen}
        onClose={() => setAboutOpen(false)}
        config={aboutConfig}
      />
      {/* Feedback modal (z-[1000] clears the SettingsDrawer's z-900/901) */}
      <FeedbackForm
        isOpen={feedbackOpen}
        onClose={handleCloseFeedback}
        initialType={feedbackConfig.type}
        initialCategory={feedbackConfig.category}
        initialError={feedbackConfig.error}
      />
      {/* Floating feedback trigger button */}
      <FeedbackButton onOpen={handleOpenFeedback} />
    </div>
  );
}
